import React, { useState, useEffect } from "react";
import { AeraButton } from "@/app/components/ui/AeraButton";
import Modal from "@/app/components/ui/Modal";
import axios from "axios";
import { useDynamicFormSubmit } from "@/hooks/useDynamicFormSubmit";
import { getProductIdForService } from "@/lib/getProductIdForService";

interface DynamicSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  config: any;
  title: string;
  serviceId?: string;
  stepTitle?: string;
  isEmbeddedInModal?: boolean;
  initialValues?: Record<string, any>;
  autoSubmit?: boolean;
  onSubmittingChange?: (submitting: boolean) => void;
}

export const DynamicSetupModal: React.FC<DynamicSetupModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  config,
  title,
  serviceId,
  stepTitle,
  isEmbeddedInModal = false,
  initialValues,
  autoSubmit = false,
  onSubmittingChange,
}) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [fieldPlaceholders, setFieldPlaceholders] = useState<
    Record<string, string>
  >({});

  // Auto-submit state
  const autoSubmitTriggeredRef = React.useRef(false);

  // Use custom hook for form submission
  // Line Setup should not refresh, Business Setup should refresh
  const isLineSetup = stepTitle === "Line Setup" || title.includes("Line");
  const { isSubmitting, handleSubmit: handleFormSubmit } = useDynamicFormSubmit(
    {
      onSubmit,
      onClose,
      shouldRefresh: !isLineSetup, // Don't refresh for Line Setup
    },
  );

  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  const [parsedFields, setParsedFields] = useState<any[]>([]);

  useEffect(() => {
    // Initialize form data based on config properties
    const initialData: Record<string, any> = {};

    if (!config) {
      setFormData(initialData);
      setParsedFields([]);
      return;
    }

    let currentConfig = config;

    // Parse if string
    if (typeof config === "string") {
      try {
        currentConfig = JSON.parse(config);
      } catch (e) {
        console.error("Failed to parse config string:", e);
        setFormData(initialData);
        setParsedFields([]);
        return;
      }
    }

    if (currentConfig.requests && currentConfig.requests.properties) {
      const properties = currentConfig.requests.properties;

      // Only set project_id if it exists in the config properties
      if (serviceId && properties.project_id) {
        initialData["project_id"] = getProductIdForService(serviceId);
      }

      // Only set service_id if it exists in the config properties
      if (serviceId && properties.service_id) {
        initialData["service_id"] = serviceId;
      }
    }

    // Merge provided initial values
    if (initialValues) {
      Object.assign(initialData, initialValues);
    }

    setFormData(initialData);

    // Handle JSON Schema style (requests.properties)
    if (currentConfig.requests && currentConfig.requests.properties) {
      const properties = currentConfig.requests.properties;
      const requiredFields = currentConfig.requests.required || [];

      const fields = Object.keys(properties)
        .map((key) => {
          const prop = properties[key];

          // For Credentials Setup, only show LINE-related fields
          if (stepTitle === "Credentials Setup") {
            const isLineField = key.toLowerCase().includes("line");
            if (!isLineField) {
              return null; // Hide non-LINE fields
            }
          }
          // For Knowledge Setup, only show business_info field
          else if (stepTitle === "Business Setup") {
            if (key !== "business_info") {
              return null; // Hide all fields except business_info
            }
          } else {
            // For other steps, check user_input flag
            // Exception: Always show project_id if present
            if (prop.user_input === false && key !== "project_id") {
              return null;
            }
          }
          // Use textarea for business_info field (long-form text)
          const fieldType = key === "business_info" ? "textarea" : "text";

          return {
            name: key,
            label: prop.label || key, // Use label or key as fallback
            type: fieldType, // Default to text, but textarea for business_info
            required: requiredFields.includes(key),
            placeholder:
              prop.placeholder || prop.description || prop.label || key,
            description: prop.description,
          };
        })
        .filter(Boolean); // Filter out nulls

      setParsedFields(fields);
    } else if (currentConfig.fields) {
      // Handle legacy/simple structure
      setParsedFields(currentConfig.fields);
    } else {
      console.warn("Unknown config structure:", currentConfig);
      setParsedFields([]);
    }
  }, [config, serviceId, stepTitle]);

  // Data Prefetching Logic
  useEffect(() => {
    if (!config) return;

    let currentConfig = config;
    if (typeof config === "string") {
      try {
        currentConfig = JSON.parse(config);
      } catch (e) {
        return;
      }
    }

    if (currentConfig.prefetch_url) {
      const fetchInitialData = async () => {
        try {
          const headers: Record<string, string> = {
            "Content-Type": "application/json",
          };
          const token = localStorage.getItem("auth_token");
          if (token) {
            const cleanToken = token.trim().startsWith("Bearer ")
              ? token.trim().substring(7)
              : token.trim();
            headers["Authorization"] = `Bearer ${cleanToken}`;
          }

          // Using axios with 120 second timeout
          const response = await axios.post(
            "/api/proxy",
            {
              url: currentConfig.prefetch_url,
              method: currentConfig.prefetch_method || "GET",
              headers,
            },
            {
              timeout: 120000, // 120 seconds
            },
          );

          const result = response.data;

          // Use prefetched data as placeholders, NOT formData
          setFieldPlaceholders((prev) => ({ ...prev, ...result }));
        } catch (error) {
          console.error("[DynamicSetupModal] Prefetch error:", error);
        }
      };

      fetchInitialData();
    }
  }, [config]);

  const fields = parsedFields;

  const handleChange = (name: string, value: any) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    handleFormSubmit(e, formData);
  };

  // Auto-submit logic
  useEffect(() => {
    if (autoSubmit && isOpen && !autoSubmitTriggeredRef.current) {
      // Check if initialValues are present in formData (basic check)
      if (
        initialValues &&
        !Object.keys(initialValues).every((k) => formData[k] !== undefined)
      ) {
        return;
      }

      const timer = setTimeout(() => {
        autoSubmitTriggeredRef.current = true;
        // Create a synthetic event
        const syntheticEvent = { preventDefault: () => {} } as React.FormEvent;
        handleFormSubmit(syntheticEvent, formData);
      }, 500); // 500ms delay
      return () => clearTimeout(timer);
    }
  }, [autoSubmit, isOpen, formData, initialValues, handleFormSubmit]); // Dependencies ensure we have data before submitting

  const footer = (
    <>
      <AeraButton
        variant="outline"
        fontWeight="medium"
        onClick={onClose}
        className="flex-1"
      >
        ยกเลิก
      </AeraButton>
      <AeraButton
        variant="primary"
        fontWeight="medium"
        type="submit"
        form="dynamic-setup-form"
        loading={isSubmitting}
        disabled={isSubmitting}
        className="flex-1"
      >
        {isSubmitting ? "กำลังบันทึก..." : "บันทึกการตั้งค่า"}
      </AeraButton>
    </>
  );

  const body = (
    <div className="mx-auto w-full max-w-3xl">
      <form
        id="dynamic-setup-form"
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        {fields.length === 0 && (
          <p className="text-sm text-aera-gray">
            ไม่มีการตั้งค่าที่จำเป็นสำหรับขั้นตอนนี้
          </p>
        )}

        {fields.map((field: any, index: number) => (
          <div key={index} className="space-y-2">
            <label className="block text-sm font-bold text-aera-charcoal">
              {field.label}{" "}
              {field.required && <span className="text-aera-red">*</span>}
            </label>
            {field.description && (
              <p className="text-xs text-aera-gray mb-1">{field.description}</p>
            )}

            {field.type === "textarea" ? (
              <textarea
                required={field.required}
                value={formData[field.name] || ""}
                onChange={(e) => handleChange(field.name, e.target.value)}
                placeholder={fieldPlaceholders[field.name] || field.placeholder}
                className="w-full text-sm rounded-lg border border-aera-lightgray/80 text-aera-charcoal placeholder:text-aera-gray p-3 focus:ring-2 focus:ring-aera-amber focus:border-transparent outline-none min-h-[120px] shadow-sm"
              />
            ) : field.type === "select" ? (
              <select
                required={field.required}
                value={formData[field.name] || ""}
                onChange={(e) => handleChange(field.name, e.target.value)}
                className="w-full text-sm rounded-lg border border-aera-lightgray/80 text-aera-gray p-3 focus:ring-2 focus:ring-aera-amber focus:border-transparent outline-none bg-aera-white shadow-sm"
              >
                <option value="">เลือกตัวเลือก...</option>
                {field.options?.map((opt: any, idx: number) => (
                  <option key={idx} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : (
              // Default to text input
              <input
                type={field.type || "text"}
                required={field.required}
                value={formData[field.name] || ""}
                onChange={(e) => handleChange(field.name, e.target.value)}
                placeholder={fieldPlaceholders[field.name] || field.placeholder}
                className="w-full text-sm rounded-lg bg-aera-white border border-aera-lightgray/80 text-aera-charcoal placeholder:text-aera-gray p-3 focus:ring-1 focus:ring-aera-amber focus:border-transparent outline-none"
              />
            )}
          </div>
        ))}
      </form>

      {/* Warning Message when submitting - Only for Business Setup */}
      {isSubmitting && stepTitle === "Business Setup" && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mt-4">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-amber-600 shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-bold text-amber-900">กำลังดำเนินการ</p>
              <p className="text-xs text-amber-800 mt-1">
                ห้ามปิดหน้าต่างนี้จนกว่าจะเสร็จสิ้น
                กระบวนการนี้อาจใช้เวลาสักครู่
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (isEmbeddedInModal) {
    if (!isOpen) return null;
    return body;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={footer}
      size="lg"
      preventBackdropClose={isSubmitting}
      closeDisabled={isSubmitting}
    >
      {body}
    </Modal>
  );
};
